import { ChevronUp, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SortableThProps {
  label: string;
  /** This column's sort key (must match a server allow-list key). */
  sortKey: string;
  /** The currently active sort key. */
  sort: string;
  /** The currently active sort direction. */
  dir: 'asc' | 'desc';
  /** Called with `sortKey` when the header is clicked. */
  onToggle: (key: string) => void;
  /** Column alignment — 'right' for money/quantity columns. Defaults to 'left'. */
  align?: 'left' | 'right';
  className?: string;
}

/**
 * A clickable table header that drives column sorting. Shows ▲/▼ on the active
 * column and toggles direction via `onToggle` (wired to the hook's `toggleSort`).
 */
export function SortableTh({
  label,
  sortKey,
  sort,
  dir,
  onToggle,
  align = 'left',
  className,
}: SortableThProps) {
  const active = sort === sortKey;
  return (
    <th
      className={cn(
        'px-4 py-3 font-medium text-muted-foreground',
        align === 'right' ? 'text-right' : 'text-left',
        className,
      )}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onToggle(sortKey)}
        className={cn(
          'inline-flex items-center gap-1 px-1 py-0.5 rounded transition-colors hover:text-foreground',
          align === 'right' ? '-mr-1 flex-row-reverse' : '-ml-1',
          active && 'text-foreground',
        )}
      >
        <span>{label}</span>
        {active ? (
          dir === 'asc' ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )
        ) : (
          <ChevronUp className="h-3.5 w-3.5 opacity-0 group-hover:opacity-40" />
        )}
      </button>
    </th>
  );
}
